import React, { useEffect, useState, FC } from 'react';
import { UpdateTeamRequest, TeamInputAtlassianOrgStatusEnum } from '@atlassiansox/phobos-client-ts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Field } from '@atlaskit/form';
import { AsyncSelect } from '@atlaskit/select';
import Button, { LoadingButton } from '@atlaskit/button';
import EditIcon from '@atlaskit/icon/glyph/edit';
import CopyIcon from '@atlaskit/icon/glyph/copy';
import ToolTip from '@atlaskit/tooltip';

import { apis, apiResolve } from '@/services';
import { Item, formatOptionLabel, FormFieldTooltipLabel, useActions } from '@/components/primitive';
import { spaceTopComfortable, spaceGapCompact, flexStart, cursorPointer } from '@/styles';
import { useFetchErrorController, useAutoCompleteController } from '@/controllers';
import { Confirm } from '@/components/composed';
import { helperCopy } from '@/components/composed/forms/copy';

import * as styles from './styles';
import { OrgSelectOptionType, TeamOrgModelPropsType } from './types';
import { testIds } from './constants';
import UnassignedWarningMessage from './unassigned-warning-message';
import TeamOrgStatus from './team-org-status';

const TeamOrgModel: FC<TeamOrgModelPropsType> = ({ data, dataOrg }) => {
  const { publicId } = data;
  const { onError } = useFetchErrorController();
  const loadTeams = useAutoCompleteController('org', { optionValuekey: 'orgId' });

  const [orgSelectOption, setOrgSelectOption] = useState<OrgSelectOptionType>();
  const [isAssignLoading, setIsAssignLoading] = useState<boolean>(false);
  const [isAssignActionOn, setIsAssignActionOn] = useState<boolean>(false);
  const [isVerifyLoading, setIsVerifyLoading] = useState<boolean>(false);
  const [isVerifyActionOn, setIsVerifyActionOn] = useState<boolean>(false);

  const { onCopy } = useActions(dataOrg.orgId, undefined, 'team');

  const queryClient = useQueryClient();
  const mutation = useMutation(
    (request: UpdateTeamRequest) => apiResolve(apis.team.updateTeam(request)),
    {
      onError,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['team'] });
        setIsAssignLoading(false);
        setIsVerifyLoading(false);
      },
    }
  );

  const handleOrgSelectChange = (e: OrgSelectOptionType) => {
    setOrgSelectOption(e);
    setIsAssignLoading(true);

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { opsgenieTeamId, ...rest } = data;
    mutation.mutate({
      publicId,
      teamInput: {
        ...rest,
        atlassianOrgId: e.value,
        atlassianOrgStatus: TeamInputAtlassianOrgStatusEnum.Assigned,
      },
    });
  };

  const handleVerifyClick = async () => {
    const verify: boolean = await Confirm({
      body: (
        <>
          Please confirm <b>{dataOrg.orgName}</b> is the Atlassian Organisation Team you would like
          to verify
        </>
      ),
    });
    if (!verify) return;

    setIsVerifyLoading(true);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { opsgenieTeamId, ...rest } = data;
    mutation.mutate({
      publicId,
      teamInput: {
        ...rest,
        atlassianOrgStatus: TeamInputAtlassianOrgStatusEnum.Verified,
      },
    });
  };

  const hasOrg = !!data.atlassianOrgId;
  const hasOrgVerified =
    hasOrg && data.atlassianOrgStatus === TeamInputAtlassianOrgStatusEnum.Verified;
  const hasOrgActionBar = (hasOrg && !hasOrgVerified) || isAssignActionOn || isVerifyActionOn;
  const hasOrgWarning = !hasOrg && !hasOrgActionBar;
  const isVerifyDisabled = !hasOrg || hasOrgVerified || isAssignLoading || isVerifyLoading;

  const handleEditClick = () => {
    setIsVerifyActionOn(true);
  };

  useEffect(() => {
    if (!dataOrg.orgId) return;

    setOrgSelectOption({
      label: dataOrg.orgName,
      value: dataOrg.orgId,
      level: 'team',
    });
  }, [dataOrg]);

  useEffect(() => {
    if (!hasOrgVerified) return;

    setIsVerifyActionOn(false);
    setIsAssignActionOn(false);
  }, [hasOrgVerified]);

  return (
    <Item
      title={
        <div data-testid={testIds.sectionTitle} css={[flexStart, spaceGapCompact]}>
          <span>Atlassian Org Team</span>
          <TeamOrgStatus orgStatus={data.atlassianOrgStatus as TeamInputAtlassianOrgStatusEnum} />
        </div>
      }
    >
      {hasOrgVerified && !isVerifyActionOn && (
        <span css={[flexStart, spaceGapCompact]}>
          <span>{dataOrg?.orgName}</span>
          <span css={cursorPointer} data-testid={testIds.buttonEdit} onClick={handleEditClick}>
            <ToolTip position="top" content="Edit Org">
              <EditIcon size="small" label="Edit" />
            </ToolTip>
          </span>
          <span css={cursorPointer} onClick={onCopy}>
            <ToolTip position="top" content="Copy ID">
              <CopyIcon size="small" label="Copy ID" />
            </ToolTip>
          </span>
        </span>
      )}
      {hasOrgWarning && (
        <UnassignedWarningMessage
          onAssignClick={() => {
            setIsAssignActionOn(true);
          }}
        />
      )}
      {hasOrgActionBar && (
        <div css={styles.orgActionBarStyles} data-testid={testIds.actionBar}>
          <Field<any>
            name="team-org-select"
            label={
              <FormFieldTooltipLabel
                label="Select an Atlassian Organisation Team"
                content={helperCopy.team.atlassianOrgTeam}
              />
            }
          >
            {({ fieldProps: { id, ...rest } }) => (
              <AsyncSelect
                spacing="compact"
                formatOptionLabel={formatOptionLabel}
                placeholder="Select an Atlassian Org Team to associate..."
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...rest}
                value={orgSelectOption}
                inputId={id}
                defaultOptions
                loadOptions={loadTeams}
                onChange={handleOrgSelectChange}
                isDisabled={isAssignLoading}
                isLoading={isAssignLoading}
                filterOption={option => option.data.level === 'team'}
              />
            )}
          </Field>
          <LoadingButton
            css={spaceTopComfortable}
            isLoading={isVerifyLoading}
            isDisabled={isVerifyDisabled}
            appearance="primary"
            onClick={handleVerifyClick}
          >
            Verify association
          </LoadingButton>
          <Button
            onClick={onCopy}
            isDisabled={isVerifyDisabled}
            iconBefore={<CopyIcon size="small" label="" />}
          >
            Copy ID
          </Button>
        </div>
      )}
    </Item>
  );
};

export default TeamOrgModel;
